/**
 * Master Timeline — Unified stacked-layer SVG visualization (Screening Room design).
 *
 * Layers (top → bottom):
 *   1. Emotional Intensity Band (filled area, gold gradient)
 *   2. Dead Zone Overlays (semi-transparent gray rectangles)
 *   3. Character Presence Bars (horizontal per-character swim lanes)
 *   4. Budget Cost Spikes (inverted bars growing downward)
 *   5. Risk Diamond Markers
 *   6. Act Dividers (vertical gold lines spanning full height)
 *
 * Uses SVG for rendering, D3 for scales/math only, Framer Motion for entrance.
 * Accepts the same `{ analysis }` prop as before.
 */
import { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Eye, EyeOff, Clock } from 'lucide-react';
import { scaleLinear, scaleBand } from 'd3';

/* ═══════════════════════════════════════════════════════════════════
   Constants & palettes
   ═══════════════════════════════════════════════════════════════════ */

const BUDGET_TIER_COLORS = {
  minimal: '#22C55E',
  low: '#3B82F6',
  moderate: '#F59E0B',
  high: '#FF6B35',
  very_high: '#EF4444',
};

const RISK_COLORS = {
  low: '#22C55E',
  moderate: '#F59E0B',
  high: '#FF6B35',
  critical: '#EF4444',
};

const RISK_DIAMOND_SIZE = {
  critical: 10,
  high: 7,
  moderate: 5,
  low: 4,
};

const CHARACTER_PALETTE = [
  '#C9A84C', '#14B8A6', '#EC4899', '#A855F7', '#3B82F6', '#F97316',
];

const GOLD = '#C9A84C';
const BG = '#09090B';
const GRID_LINE = '#222228';
const FONT_LABEL = "'DM Sans', sans-serif";
const FONT_MONO = "'JetBrains Mono', monospace";

/* Layer vertical allocation (px) inside the SVG */
const LAYER_HEIGHTS = {
  emotion: 80,
  deadZone: 0,      // overlays on emotion band, no extra height
  characters: 60,   // grows with character count
  budget: 50,
  risk: 28,
};
const ACT_LABEL_HEIGHT = 18;
const XAXIS_HEIGHT = 22;
const CHAR_BAR_H = 8;
const CHAR_GAP = 3;
const LEFT_GUTTER = 80;  // space for character name labels
const RIGHT_PAD = 16;
const TOP_PAD = 8;

/* Layer definitions for toggle bar */
const LAYERS = [
  { key: 'emotions', label: 'Emotions' },
  { key: 'deadZones', label: 'Dead Zones' },
  { key: 'budget', label: 'Budget' },
  { key: 'risk', label: 'Risk' },
  { key: 'characters', label: 'Characters' },
];

/* ═══════════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════════ */

function clamp(val, min, max) {
  return Math.min(Math.max(val, min), max);
}

function parseSceneRange(range) {
  if (!range) return [null, null];
  const parts = String(range).split('-').map(Number);
  return [parts[0] || null, parts[1] || parts[0] || null];
}

/** Build an SVG area path from points [{x, y}] closing down to baseline. */
function areaPath(points, baseline) {
  if (!points.length) return '';
  let d = `M${points[0].x},${baseline}`;
  points.forEach((p) => { d += ` L${p.x},${p.y}`; });
  d += ` L${points[points.length - 1].x},${baseline} Z`;
  return d;
}

/* ═══════════════════════════════════════════════════════════════════
   Sub-components
   ═══════════════════════════════════════════════════════════════════ */

function ToggleBar({ visibility, onToggle, available }) {
  return (
    <div className="flex flex-wrap gap-1.5 mb-3">
      {LAYERS.map(({ key, label }) => {
        if (!available[key]) return null;
        const active = visibility[key];
        return (
          <button
            key={key}
            onClick={() => onToggle(key)}
            className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-[11px] font-medium transition-colors ${
              active
                ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
                : 'bg-[var(--bg-app)] text-[var(--text-muted)] border border-[var(--border-subtle)] hover:text-[var(--text-secondary)]'
            }`}
          >
            {active ? <Eye size={12} /> : <EyeOff size={12} />}
            {label}
          </button>
        );
      })}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════════ */

export default function MasterTimeline({ analysis }) {
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(900);
  const [hoveredIdx, setHoveredIdx] = useState(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [selectedIdx, setSelectedIdx] = useState(null);
  const [visibility, setVisibility] = useState({
    emotions: true,
    deadZones: true,
    budget: true,
    risk: true,
    characters: true,
  });

  /* ─── Resize observer ─── */
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /* ─── Derive data safely ─── */
  const emotionalScenes = analysis?.emotional_architecture?.scenes;
  const deadZones = analysis?.dead_zones?.dead_zones;
  const budgetScenes = analysis?.budget?.scenes;
  const riskScenes = analysis?.risk?.scenes;
  const characters = analysis?.characters?.characters;
  const actStructure = analysis?.act_structure;

  const sceneSource = emotionalScenes || budgetScenes || riskScenes || [];
  const sceneCount = sceneSource.length;

  const available = useMemo(
    () => ({
      emotions: !!emotionalScenes?.length,
      deadZones: !!deadZones?.length,
      budget: !!budgetScenes?.length,
      risk: !!riskScenes?.length,
      characters: !!characters?.length,
    }),
    [emotionalScenes, deadZones, budgetScenes, riskScenes, characters],
  );

  /* ─── Visibility flags ─── */
  const showEmotions = visibility.emotions && available.emotions;
  const showDeadZones = visibility.deadZones && available.deadZones;
  const showBudget = visibility.budget && available.budget;
  const showRisk = visibility.risk && available.risk;
  const showCharacters = visibility.characters && available.characters;

  /* ─── Scene number → index ─── */
  const sceneNumberToIndex = useMemo(() => {
    const m = {};
    sceneSource.forEach((s, i) => { m[s.scene_number] = i; });
    return m;
  }, [sceneSource]);

  /* ─── Dead zone coverage ─── */
  const deadZoneCoverage = useMemo(() => {
    const coverage = new Set();
    if (!deadZones) return coverage;
    deadZones.forEach((dz) => {
      let start = dz.scene_start;
      let end = dz.scene_end;
      if (start == null || end == null) {
        const [rs, re] = parseSceneRange(dz.scene_range);
        start = start ?? rs;
        end = end ?? re;
      }
      if (start != null && end != null) {
        for (let s = start; s <= end; s++) {
          if (sceneNumberToIndex[s] != null) coverage.add(sceneNumberToIndex[s]);
        }
      }
    });
    return coverage;
  }, [deadZones, sceneNumberToIndex]);

  /* ─── Dead zone ranges (contiguous index runs for labeling) ─── */
  const deadZoneRanges = useMemo(() => {
    if (!deadZones) return [];
    const ranges = [];
    deadZones.forEach((dz) => {
      let start = dz.scene_start;
      let end = dz.scene_end;
      if (start == null || end == null) {
        const [rs, re] = parseSceneRange(dz.scene_range);
        start = start ?? rs;
        end = end ?? re;
      }
      if (start != null && end != null) {
        const startIdx = sceneNumberToIndex[start];
        const endIdx = sceneNumberToIndex[end];
        if (startIdx != null && endIdx != null) {
          ranges.push({ startIdx, endIdx });
        }
      }
    });
    return ranges;
  }, [deadZones, sceneNumberToIndex]);

  /* ─── Top characters (up to 6) ─── */
  const topCharacters = useMemo(() => {
    if (!characters?.length) return [];
    return characters.slice(0, 6).map((c, i) => ({
      ...c,
      color: CHARACTER_PALETTE[i % CHARACTER_PALETTE.length],
      sceneIndices: new Set(
        (c.scenes || [])
          .map((sn) => (typeof sn === 'number' ? sceneNumberToIndex[sn] : sceneNumberToIndex[sn?.scene_number]))
          .filter((idx) => idx != null),
      ),
    }));
  }, [characters, sceneNumberToIndex]);

  /* ─── Normalization maxima ─── */
  const maxIntensity = useMemo(() => {
    if (!emotionalScenes?.length) return 1;
    return Math.max(
      ...emotionalScenes.map((s) => s.intensity_composite || s.intensity || 0),
      1,
    );
  }, [emotionalScenes]);

  /* ─── Act boundaries ─── */
  const actLabels = useMemo(() => {
    if (!actStructure) return [];
    const { act1_end, act2a_end, act2b_end, act3_start } = actStructure;
    const lines = [];
    if (act1_end != null && sceneNumberToIndex[act1_end] != null) {
      lines.push({ idx: sceneNumberToIndex[act1_end], labels: ['ACT 1', 'ACT 2A'] });
    }
    if (act2a_end != null && sceneNumberToIndex[act2a_end] != null) {
      lines.push({ idx: sceneNumberToIndex[act2a_end], labels: ['ACT 2A', 'ACT 2B'] });
    }
    const act3Boundary = act3_start ?? act2b_end;
    if (act3Boundary != null && sceneNumberToIndex[act3Boundary] != null) {
      lines.push({ idx: sceneNumberToIndex[act3Boundary], labels: ['ACT 2B', 'ACT 3'] });
    }
    return lines;
  }, [actStructure, sceneNumberToIndex]);

  /* ═══════════════════════════════════════════════════════════════
     Layout calculations
     ═══════════════════════════════════════════════════════════════ */

  const charCount = showCharacters ? topCharacters.length : 0;
  const charLayerH = charCount > 0 ? charCount * (CHAR_BAR_H + CHAR_GAP) + 4 : 0;

  // Compute active layer heights
  const emotionH = showEmotions ? LAYER_HEIGHTS.emotion : 0;
  const budgetH = showBudget ? LAYER_HEIGHTS.budget : 0;
  const riskH = showRisk ? LAYER_HEIGHTS.risk : 0;

  const totalLayerH = emotionH + charLayerH + budgetH + riskH;
  const svgHeight = TOP_PAD + ACT_LABEL_HEIGHT + totalLayerH + XAXIS_HEIGHT;
  const plotWidth = Math.max(containerWidth - LEFT_GUTTER - RIGHT_PAD, 100);

  /* ─── D3 scales ─── */
  const xScale = useMemo(() => {
    return scaleBand()
      .domain(sceneSource.map((_, i) => i))
      .range([0, plotWidth])
      .paddingInner(0.1)
      .paddingOuter(0.05);
  }, [sceneSource, plotWidth]);

  const emotionYScale = useMemo(() => {
    return scaleLinear().domain([0, maxIntensity]).range([emotionH, 0]).clamp(true);
  }, [maxIntensity, emotionH]);

  const budgetYScale = useMemo(() => {
    return scaleLinear().domain([0, 100]).range([0, budgetH]).clamp(true);
  }, [budgetH]);

  /* ─── Y offsets for each layer ─── */
  const yOffsets = useMemo(() => {
    let y = TOP_PAD + ACT_LABEL_HEIGHT;
    const offsets = {};
    offsets.emotion = y;
    y += emotionH;
    offsets.characters = y;
    y += charLayerH;
    offsets.budget = y;
    y += budgetH;
    offsets.risk = y;
    y += riskH;
    offsets.xAxis = y;
    return offsets;
  }, [emotionH, charLayerH, budgetH, riskH]);

  /* ─── Handlers ─── */
  const handleToggle = useCallback((key) => {
    setVisibility((prev) => ({ ...prev, [key]: !prev[key] }));
  }, []);

  const handleMouseMove = useCallback((e) => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    const localX = e.clientX - rect.left - LEFT_GUTTER;
    // Determine which scene band the mouse is over
    const bandwidth = xScale.bandwidth();
    const step = xScale.step();
    for (let i = 0; i < sceneCount; i++) {
      const bx = xScale(i);
      if (localX >= bx && localX <= bx + bandwidth) {
        setHoveredIdx(i);
        setMousePos({ x: e.clientX, y: e.clientY });
        return;
      }
    }
    setHoveredIdx(null);
  }, [xScale, sceneCount]);

  const handleMouseLeave = useCallback(() => {
    setHoveredIdx(null);
  }, []);

  const handleClick = useCallback((idx) => {
    setSelectedIdx((prev) => (prev === idx ? null : idx));
  }, []);

  /* ═══════════════════════════════════════════════════════════════
     Guard
     ═══════════════════════════════════════════════════════════════ */

  if (!sceneCount) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <Clock size={20} className="text-[var(--text-disabled)] mb-2" />
        <p className="text-sm text-[var(--text-muted)]">Timeline requires scene-level data</p>
        <p className="text-xs text-[var(--text-disabled)] mt-1 max-w-xs">
          Re-run the analysis to generate per-scene emotional, budget, and risk breakdowns for the timeline.
        </p>
      </div>
    );
  }

  /* ═══════════════════════════════════════════════════════════════
     Build SVG layer data
     ═══════════════════════════════════════════════════════════════ */

  // Emotion area points
  const emotionPoints = showEmotions
    ? sceneSource.map((_, i) => {
        const es = emotionalScenes?.[i];
        const intensity = es?.intensity_composite ?? es?.intensity ?? 0;
        const cx = xScale(i) + xScale.bandwidth() / 2;
        const cy = emotionYScale(intensity);
        return { x: cx, y: cy };
      })
    : [];

  // Budget data per scene
  const budgetData = showBudget
    ? sceneSource.map((_, i) => {
        const bs = budgetScenes?.[i];
        if (!bs) return null;
        const cost = bs.relative_cost ?? 0;
        const tier = bs.cost_tier || 'low';
        return { cost, tier };
      })
    : [];

  // Risk data per scene
  const riskData = showRisk
    ? sceneSource.map((_, i) => {
        const rs = riskScenes?.[i];
        if (!rs) return null;
        return {
          level: rs.risk_level || 'low',
          score: rs.risk_score || 0,
        };
      })
    : [];

  /* ═══════════════════════════════════════════════════════════════
     Tooltip content builder
     ═══════════════════════════════════════════════════════════════ */

  function buildTooltip(idx) {
    const scene = sceneSource[idx];
    if (!scene) return null;
    const lines = [];
    lines.push({ label: `Scene ${scene.scene_number}`, bold: true });
    if (scene.slug_line) lines.push({ label: scene.slug_line });
    if (scene.page_start || scene.page_end) {
      lines.push({ label: `Pages ${scene.page_start || '?'}-${scene.page_end || '?'}` });
    }
    if (showEmotions && emotionalScenes?.[idx]) {
      const es = emotionalScenes[idx];
      lines.push({ label: `Intensity: ${Math.round(es.intensity_composite ?? es.intensity ?? 0)}`, color: GOLD });
    }
    if (showDeadZones && deadZoneCoverage.has(idx)) {
      lines.push({ label: 'Dead Zone', color: '#808080' });
    }
    if (showBudget && budgetScenes?.[idx]) {
      const bs = budgetScenes[idx];
      const tier = bs.cost_tier || 'low';
      lines.push({ label: `Budget: ${tier.replace('_', ' ')}`, color: BUDGET_TIER_COLORS[tier] });
    }
    if (showRisk && riskScenes?.[idx]) {
      const rs = riskScenes[idx];
      lines.push({ label: `Risk: ${rs.risk_level || 'low'}`, color: RISK_COLORS[rs.risk_level] });
    }
    if (showCharacters) {
      const present = topCharacters.filter((c) => c.sceneIndices.has(idx)).map((c) => c.name);
      if (present.length) lines.push({ label: present.join(', '), color: '#aaa' });
    }
    return lines;
  }

  /* ═══════════════════════════════════════════════════════════════
     Render
     ═══════════════════════════════════════════════════════════════ */

  const bandwidth = xScale.bandwidth();

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-[var(--text-primary)]">Master Timeline</h3>
        <span className="text-[10px] text-[var(--text-muted)]">{sceneCount} scenes</span>
      </div>

      {/* Toggle bar */}
      <ToggleBar visibility={visibility} onToggle={handleToggle} available={available} />

      {/* Timeline card */}
      <div
        ref={containerRef}
        className="relative rounded-lg border border-[var(--border-subtle)] overflow-hidden"
        style={{ background: BG }}
      >
        <motion.svg
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          width="100%"
          height={svgHeight}
          viewBox={`0 0 ${containerWidth} ${svgHeight}`}
          style={{ display: 'block', fontFamily: FONT_LABEL }}
          onMouseMove={handleMouseMove}
          onMouseLeave={handleMouseLeave}
        >
          <defs>
            {/* Gold gradient for emotion area */}
            <linearGradient id="mtl-emotion-grad" x1="0" x2="0" y1="0" y2="1">
              <stop offset="0%" stopColor={GOLD} stopOpacity={0.85} />
              <stop offset="100%" stopColor={GOLD} stopOpacity={0.05} />
            </linearGradient>
          </defs>

          {/* ── Background grid lines (every 10th scene) ── */}
          <g>
            {sceneSource.map((_, i) => {
              if (i % 10 !== 0 || i === 0) return null;
              const gx = LEFT_GUTTER + xScale(i) + bandwidth / 2;
              return (
                <line
                  key={`grid-${i}`}
                  x1={gx}
                  y1={TOP_PAD + ACT_LABEL_HEIGHT}
                  x2={gx}
                  y2={yOffsets.xAxis}
                  stroke={GRID_LINE}
                  strokeWidth={1}
                />
              );
            })}
          </g>

          {/* ══ Layer 6: Act Dividers (drawn first so other layers overlay) ══ */}
          <g>
            {actLabels.map((act, ai) => {
              const ax = LEFT_GUTTER + xScale(act.idx) + bandwidth + xScale.step() * xScale.paddingInner() / 2;
              return (
                <g key={`act-${ai}`}>
                  <line
                    x1={ax}
                    y1={TOP_PAD + ACT_LABEL_HEIGHT}
                    x2={ax}
                    y2={yOffsets.xAxis}
                    stroke={GOLD}
                    strokeWidth={2}
                    opacity={0.7}
                  />
                  {/* Left label */}
                  <text
                    x={ax - 8}
                    y={TOP_PAD + 12}
                    textAnchor="end"
                    fill={GOLD}
                    fontSize={10}
                    fontFamily={FONT_LABEL}
                    fontWeight={600}
                    opacity={0.8}
                  >
                    {act.labels[0]}
                  </text>
                  {/* Right label */}
                  <text
                    x={ax + 8}
                    y={TOP_PAD + 12}
                    textAnchor="start"
                    fill={GOLD}
                    fontSize={10}
                    fontFamily={FONT_LABEL}
                    fontWeight={600}
                    opacity={0.8}
                  >
                    {act.labels[1]}
                  </text>
                </g>
              );
            })}
          </g>

          {/* ══ Layer 1: Emotional Intensity Band ══ */}
          {showEmotions && emotionPoints.length > 0 && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.1 }}
              transform={`translate(${LEFT_GUTTER}, ${yOffsets.emotion})`}
            >
              <path
                d={areaPath(emotionPoints, emotionH)}
                fill="url(#mtl-emotion-grad)"
              />
              {/* Top stroke line for definition */}
              <polyline
                points={emotionPoints.map((p) => `${p.x},${p.y}`).join(' ')}
                fill="none"
                stroke={GOLD}
                strokeWidth={1.5}
                opacity={0.6}
              />
              {/* Right-side label */}
              <text
                x={plotWidth + 4}
                y={emotionH / 2 + 4}
                fill="#666"
                fontSize={9}
                fontFamily={FONT_LABEL}
              >
                Intensity
              </text>
            </motion.g>
          )}

          {/* ══ Layer 2: Dead Zone Overlays ══ */}
          {showDeadZones && deadZoneRanges.length > 0 && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 0.2 }}
              transform={`translate(${LEFT_GUTTER}, ${yOffsets.emotion})`}
            >
              {deadZoneRanges.map((dz, di) => {
                const rx = xScale(dz.startIdx);
                const rw = xScale(dz.endIdx) + bandwidth - rx;
                const rh = emotionH > 0 ? emotionH : 20;
                return (
                  <g key={`dz-${di}`}>
                    <rect
                      x={rx}
                      y={0}
                      width={rw}
                      height={rh}
                      fill="#808080"
                      opacity={0.3}
                      rx={2}
                    />
                    <text
                      x={rx + rw / 2}
                      y={10}
                      textAnchor="middle"
                      fill="#aaa"
                      fontSize={8}
                      fontFamily={FONT_LABEL}
                      fontWeight={500}
                    >
                      Dead Zone
                    </text>
                  </g>
                );
              })}
            </motion.g>
          )}

          {/* ══ Layer 3: Character Presence Bars ══ */}
          {showCharacters && topCharacters.length > 0 && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.3 }}
            >
              {topCharacters.map((char, ci) => {
                const cy = yOffsets.characters + ci * (CHAR_BAR_H + CHAR_GAP) + 2;
                return (
                  <g key={char.name}>
                    {/* Character name label */}
                    <text
                      x={LEFT_GUTTER - 6}
                      y={cy + CHAR_BAR_H / 2 + 3}
                      textAnchor="end"
                      fill="#888"
                      fontSize={9}
                      fontFamily={FONT_LABEL}
                    >
                      {char.name.length > 10 ? char.name.slice(0, 9) + '\u2026' : char.name}
                    </text>
                    {/* Presence segments */}
                    {sceneSource.map((_, si) => {
                      const present = char.sceneIndices.has(si);
                      if (!present) return null;
                      return (
                        <rect
                          key={si}
                          x={LEFT_GUTTER + xScale(si)}
                          y={cy}
                          width={bandwidth}
                          height={CHAR_BAR_H}
                          fill={char.color}
                          opacity={0.75}
                          rx={1}
                        />
                      );
                    })}
                  </g>
                );
              })}
            </motion.g>
          )}

          {/* ══ Layer 4: Budget Cost Spikes (inverted bars) ══ */}
          {showBudget && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              transform={`translate(${LEFT_GUTTER}, ${yOffsets.budget})`}
            >
              {budgetData.map((bd, i) => {
                if (!bd) return null;
                const bx = xScale(i);
                const bh = budgetYScale(bd.cost);
                const color = BUDGET_TIER_COLORS[bd.tier] || '#888';
                return (
                  <rect
                    key={i}
                    x={bx}
                    y={0}
                    width={bandwidth}
                    height={Math.max(bh, 1)}
                    fill={color}
                    opacity={0.8}
                    rx={1}
                  />
                );
              })}
              {/* Side label */}
              <text
                x={-6}
                y={budgetH / 2 + 3}
                textAnchor="end"
                fill="#666"
                fontSize={9}
                fontFamily={FONT_LABEL}
              >
                Cost
              </text>
            </motion.g>
          )}

          {/* ══ Layer 5: Risk Diamond Markers ══ */}
          {showRisk && (
            <motion.g
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              transform={`translate(${LEFT_GUTTER}, ${yOffsets.risk})`}
            >
              {riskData.map((rd, i) => {
                if (!rd || rd.score === 0) return null;
                const cx = xScale(i) + bandwidth / 2;
                const cy = riskH / 2;
                const size = RISK_DIAMOND_SIZE[rd.level] || 4;
                const color = RISK_COLORS[rd.level] || RISK_COLORS.low;
                return (
                  <rect
                    key={i}
                    x={cx - size / 2}
                    y={cy - size / 2}
                    width={size}
                    height={size}
                    fill={color}
                    transform={`rotate(45 ${cx} ${cy})`}
                    opacity={0.9}
                  />
                );
              })}
              {/* Side label */}
              <text
                x={-6}
                y={riskH / 2 + 3}
                textAnchor="end"
                fill="#666"
                fontSize={9}
                fontFamily={FONT_LABEL}
              >
                Risk
              </text>
            </motion.g>
          )}

          {/* ══ X-Axis: Scene Numbers ══ */}
          <g transform={`translate(${LEFT_GUTTER}, ${yOffsets.xAxis})`}>
            {/* Top border */}
            <line x1={0} y1={0} x2={plotWidth} y2={0} stroke={GRID_LINE} strokeWidth={1} />
            {sceneSource.map((scene, i) => {
              // Show every scene number, or skip some if too dense
              const step = sceneCount > 60 ? 5 : sceneCount > 30 ? 2 : 1;
              if (i % step !== 0 && i !== sceneCount - 1) return null;
              return (
                <text
                  key={i}
                  x={xScale(i) + bandwidth / 2}
                  y={14}
                  textAnchor="middle"
                  fill="#666"
                  fontSize={10}
                  fontFamily={FONT_MONO}
                >
                  {scene.scene_number}
                </text>
              );
            })}
          </g>

          {/* ══ Hover highlight column ══ */}
          {hoveredIdx !== null && (
            <g>
              <rect
                x={LEFT_GUTTER + xScale(hoveredIdx)}
                y={TOP_PAD + ACT_LABEL_HEIGHT}
                width={bandwidth}
                height={yOffsets.xAxis - TOP_PAD - ACT_LABEL_HEIGHT}
                fill="#fff"
                opacity={0.06}
              />
              <line
                x1={LEFT_GUTTER + xScale(hoveredIdx) + bandwidth / 2}
                y1={TOP_PAD + ACT_LABEL_HEIGHT}
                x2={LEFT_GUTTER + xScale(hoveredIdx) + bandwidth / 2}
                y2={yOffsets.xAxis}
                stroke="#fff"
                strokeWidth={1}
                opacity={0.15}
                strokeDasharray="3,2"
              />
            </g>
          )}

          {/* ══ Selected scene highlight ══ */}
          {selectedIdx !== null && (
            <rect
              x={LEFT_GUTTER + xScale(selectedIdx)}
              y={TOP_PAD + ACT_LABEL_HEIGHT}
              width={bandwidth}
              height={yOffsets.xAxis - TOP_PAD - ACT_LABEL_HEIGHT}
              fill={GOLD}
              opacity={0.08}
              stroke={GOLD}
              strokeWidth={1}
              strokeOpacity={0.3}
            />
          )}

          {/* ══ Invisible click targets per scene ══ */}
          {sceneSource.map((_, i) => (
            <rect
              key={`click-${i}`}
              x={LEFT_GUTTER + xScale(i)}
              y={TOP_PAD + ACT_LABEL_HEIGHT}
              width={bandwidth}
              height={yOffsets.xAxis - TOP_PAD - ACT_LABEL_HEIGHT}
              fill="transparent"
              style={{ cursor: 'pointer' }}
              onClick={() => handleClick(i)}
            />
          ))}
        </motion.svg>

        {/* ── Floating tooltip ── */}
        <AnimatePresence>
          {hoveredIdx !== null && (
            <motion.div
              initial={{ opacity: 0, y: 4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 4 }}
              transition={{ duration: 0.12 }}
              className="fixed z-50 pointer-events-none rounded-lg border px-3 py-2 text-xs shadow-xl"
              style={{
                left: mousePos.x,
                top: mousePos.y - 12,
                transform: 'translate(-50%, -100%)',
                background: '#1A1A1F',
                borderColor: '#333',
                fontFamily: FONT_LABEL,
                maxWidth: 260,
              }}
            >
              {buildTooltip(hoveredIdx)?.map((line, li) => (
                <p
                  key={li}
                  className={line.bold ? 'font-semibold mb-0.5' : ''}
                  style={{
                    color: line.color || (line.bold ? '#E5E5E5' : '#999'),
                    fontSize: line.bold ? 12 : 11,
                    lineHeight: '1.4',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {line.label}
                </p>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* ── Legends ── */}
      <div className="flex flex-wrap gap-x-5 gap-y-1.5 px-1">
        {showCharacters && topCharacters.length > 0 && (
          <div className="flex items-center gap-2">
            {topCharacters.map((char) => (
              <span
                key={char.name}
                className="flex items-center gap-1 text-[9px] text-[var(--text-muted)]"
              >
                <span
                  className="w-2 h-2 rounded-full inline-block"
                  style={{ backgroundColor: char.color }}
                />
                {char.name}
              </span>
            ))}
          </div>
        )}
        {showBudget && (
          <div className="flex items-center gap-2 text-[9px] text-[var(--text-muted)]">
            <span className="font-medium">Budget:</span>
            {Object.entries(BUDGET_TIER_COLORS).map(([tier, color]) => (
              <span key={tier} className="flex items-center gap-0.5">
                <span className="w-2 h-2 rounded-sm inline-block" style={{ backgroundColor: color }} />
                {tier === 'very_high' ? 'V.High' : tier.charAt(0).toUpperCase() + tier.slice(1)}
              </span>
            ))}
          </div>
        )}
        {showRisk && (
          <div className="flex items-center gap-2 text-[9px] text-[var(--text-muted)]">
            <span className="font-medium">Risk:</span>
            {Object.entries(RISK_COLORS).map(([level, color]) => (
              <span key={level} className="flex items-center gap-0.5">
                <span
                  className="w-2 h-2 inline-block"
                  style={{
                    backgroundColor: color,
                    transform: 'rotate(45deg)',
                  }}
                />
                {level.charAt(0).toUpperCase() + level.slice(1)}
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
